import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

export default function MyTablePage() {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null); // { table, startTime, endTime, hours }
  const [timeLeft, setTimeLeft] = useState("");
  const [percentage, setPercentage] = useState(100);
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    if (!currentUser) navigate("/login");
  }, [currentUser, navigate]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!currentUser?.uid) return;
      try {
        const todayStart = dayjs().startOf("day").toDate();
        const snap = await getDocs(
          query(
            collection(db, "visiteur"),
            where("FirebaseId", "==", currentUser.uid),
            where("sortie", "==", false),
            where("startTime", ">=", todayStart)
          )
        );
        if (!snap.empty) {
          const data = snap.docs[0].data();
          const startTime = dayjs(data.startTime.toDate());
          const endTime = startTime.add(data.hours, "hour");
          setSession({ table: data.table, startTime, endTime, hours: data.hours });
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchSession();
  }, [currentUser]);

  // Countdown tick
  useEffect(() => {
    if (!session) return;
    const { startTime, endTime } = session;
    const totalDuration = endTime.diff(startTime);

    const interval = setInterval(() => {
      const now = dayjs();
      const diff = endTime.diff(now);
      if (diff > 0) {
        const d = dayjs.duration(diff);
        const h = String(d.hours()).padStart(2, "0");
        const m = String(d.minutes()).padStart(2, "0");
        const s = String(d.seconds()).padStart(2, "0");
        setTimeLeft(`${h}:${m}:${s}`);
        setPercentage(Math.max(0, Math.min(100, (diff / totalDuration) * 100)));
      } else {
        setTimeLeft("00:00:00");
        setPercentage(0);
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [session]);

  const isExpired = timeLeft === "00:00:00";
  const statusColor = !session || isExpired ? t.text3
    : percentage > 50 ? t.green
    : percentage > 20 ? t.amber
    : t.red;
  const statusBg = !session || isExpired ? t.bg3
    : percentage > 50 ? t.greenBg
    : percentage > 20 ? t.amberBg
    : t.redBg;
  const statusLabel = !session ? "Aucune session active"
    : isExpired ? "Session terminée"
    : percentage > 50 ? "Temps restant"
    : percentage > 20 ? "Attention au temps"
    : "Temps presque écoulé";

  if (!currentUser) return null;

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => navigate("/")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← Retour
            </button>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Ma Table</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Temps de session restant</div>
            </div>
          </div>
          <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
            <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
          </button>
        </div>

        <div style={{ padding: "28px 18px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>

          {loading ? (
            <div style={{ textAlign: "center", paddingTop: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⏱️</div>
              <div style={{ fontSize: 14, color: t.text2 }}>Recherche de ta session...</div>
            </div>
          ) : !session ? (
            // ── No active session ──
            <>
              <div style={{ width: 72, height: 72, borderRadius: 20, background: t.bg3, border: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36 }}>
                😴
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: t.text1, marginBottom: 6 }}>Aucune session active</div>
                <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>
                  Tu n'as pas de session en cours aujourd'hui.<br />
                  Présente-toi à l'accueil pour démarrer une session.
                </div>
              </div>
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "14px 18px", width: "100%", textAlign: "center" }}>
                <div style={{ fontSize: 12, color: t.text2 }}>Les sessions sont enregistrées à ton arrivée par le staff WeStudy.</div>
              </div>
            </>
          ) : (
            // ── Active session ──
            <>
              {/* Table badge */}
              {session.table && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: t.blueBg, border: `1px solid ${t.blue}33`, borderRadius: 20, padding: "5px 14px" }}>
                  <span style={{ fontSize: 14 }}>🪑</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.blueText }}>Table {session.table}</span>
                </div>
              )}

              {/* Status badge */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: statusBg, border: `1px solid ${statusColor}33`, borderRadius: 20, padding: "6px 16px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor, animation: !isExpired ? "pulse 2s infinite" : "none" }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: statusColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {statusLabel}
                </span>
              </div>

              {/* Timer card */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 20, padding: "32px 24px", width: "100%", textAlign: "center", position: "relative", overflow: "hidden" }}>
                {/* Progress bar */}
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 5, background: t.bg3 }}>
                  <div style={{ height: "100%", background: statusColor, width: `${percentage}%`, transition: "width 1s linear", borderRadius: "0 3px 3px 0" }} />
                </div>

                <div style={{ fontSize: 56, fontWeight: 700, color: statusColor, letterSpacing: "0.05em", fontVariantNumeric: "tabular-nums", fontFamily: "'SF Mono', 'Monaco', 'Courier New', monospace", lineHeight: 1 }}>
                  {timeLeft || "00:00:00"}
                </div>

                <div style={{ display: "flex", justifyContent: "center", gap: 28, marginTop: 14, fontSize: 11, color: t.text3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px" }}>
                  <span>Heures</span>
                  <span>Minutes</span>
                  <span>Secondes</span>
                </div>
              </div>

              {/* Session details */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "14px 18px", width: "100%" }}>
                {[
                  { label: "🕐 Début", value: session.startTime.format("HH:mm") },
                  { label: "🕔 Fin prévue", value: session.endTime.format("HH:mm") },
                  { label: "⏱️ Durée totale", value: `${session.hours}h` },
                  { label: "📊 Restant", value: `${Math.round(percentage)}%` },
                ].map(row => (
                  <div key={row.label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 12, color: t.text2 }}>{row.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: t.text1 }}>{row.value}</span>
                  </div>
                ))}
              </div>

              {/* Expired message */}
              {isExpired && (
                <div style={{ background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 14, padding: "14px 18px", width: "100%", textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: t.red, marginBottom: 4 }}>Session expirée</div>
                  <div style={{ fontSize: 12, color: t.text2 }}>Merci de te présenter à l'accueil pour régulariser ta sortie.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4",
    amber: "#d97706", amberBg: "#fffbeb",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16",
    amber: "#fbbf24", amberBg: "#2a1e07",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
};
